import { create } from 'zustand'

export interface WhatsAppInstance {
  id: string
  instance_name: string
  status: 'connected' | 'disconnected' | 'connecting'
  phone_number?: string
  profile_pic_url?: string
  qr_code?: string
  is_default?: boolean
  welcome_message?: string
  default_department_id?: string | null
  default_flow_id?: string | null
  auto_close_message?: string
  notification_group_jid?: string | null
  meeting_alert_minutes?: number
  bot_timeout_minutes?: number
  bot_timeout_message?: string | null
  bot_timeout_department_id?: string | null
}

export interface ChatDepartment {
  id: string
  name: string
  description?: string
  color: string
}

export interface ChatTicket {
  id: string
  instance_id: string
  contact_number: string
  contact_name: string
  contact_pic_url?: string
  cliente_id?: string
  department_id?: string
  department_name?: string
  department_color?: string
  assigned_to?: string
  assigned_name?: string
  status: 'pending' | 'open' | 'closed' | 'deleted'
  unread_count: number
  last_message_at: string
}

export interface ChatMessage {
  id: string
  ticket_id: string
  message_id: string
  sender_type: 'contact' | 'user' | 'bot'
  sender_id?: string
  sender_name?: string
  content: string
  media_url?: string
  media_type?: string
  is_read: boolean
  created_at: string
}

interface ChatStore {
  instances: WhatsAppInstance[]
  departments: ChatDepartment[]
  tickets: ChatTicket[]
  messages: Record<string, ChatMessage[]> // ticketId -> messages array
  activeTicketId: string | null
  activeListTab: 'mine' | 'department' | 'bot' | 'closed' // Aba atual da lista de tickets
  
  presences: Record<string, 'composing' | 'recording'> // ticketId or contactNumber -> presence type
  
  setInstances: (instances: WhatsAppInstance[]) => void
  updateInstance: (instanceName: string, data: Partial<WhatsAppInstance>) => void
  removeInstance: (instanceName: string) => void
  
  setTickets: (tickets: ChatTicket[]) => void
  setDepartments: (departments: ChatDepartment[]) => void
  addTicket: (ticket: ChatTicket) => void
  updateTicket: (ticketId: string, data: Partial<ChatTicket>) => void
  removeTicket: (ticketId: string) => void
  
  setActiveTicket: (ticketId: string | null) => void
  setActiveListTab: (tab: 'mine' | 'department' | 'bot' | 'closed') => void
  
  setMessages: (ticketId: string, messages: ChatMessage[]) => void
  prependMessages: (ticketId: string, messages: ChatMessage[]) => void
  addMessage: (ticketId: string, message: ChatMessage) => void
  setPresence: (key: string, presence: 'composing' | 'recording' | 'paused' | null) => void
}

const presenceTimers = new Map<string, ReturnType<typeof setTimeout>>()

export const useChatStore = create<ChatStore>((set) => ({
  instances: [],
  departments: [],
  tickets: [],
  messages: {},
  activeTicketId: null,
  activeListTab: 'mine',
  presences: {},

  setInstances: (instances) => set({ instances }),
  setDepartments: (departments) => set({ departments }),
  
  updateInstance: (instanceName, data) => set((state) => ({
    instances: state.instances.map(inst => 
      inst.instance_name === instanceName ? { ...inst, ...data } : inst
    )
  })),

  removeInstance: (instanceName) => set((state) => ({
    instances: state.instances.filter(inst => inst.instance_name !== instanceName)
  })),

  setTickets: (tickets) => set({ tickets }),
  
  addTicket: (ticket) => set((state) => {
    const exists = state.tickets.find(t => t.id === ticket.id)
    if (exists) {return state}
    return { tickets: [ticket, ...state.tickets] }
  }),
  
  updateTicket: (ticketId, data) => set((state) => ({
    tickets: state.tickets.map(t => 
      t.id === ticketId ? { ...t, ...data } : t
    )
  })),

  removeTicket: (ticketId) => set((state) => ({
    tickets: state.tickets.filter(t => t.id !== ticketId),
    activeTicketId: state.activeTicketId === ticketId ? null : state.activeTicketId
  })),

  setActiveTicket: (ticketId) => set({ activeTicketId: ticketId }),
  setActiveListTab: (tab) => set({ activeListTab: tab }),

  setMessages: (ticketId, messages) => set((state) => ({
    messages: {
      ...state.messages,
      [ticketId]: messages
    }
  })),

  prependMessages: (ticketId, messages) => set((state) => {
    const currentMsgs = state.messages[ticketId] || []
    return {
      messages: {
        ...state.messages,
        [ticketId]: [...messages, ...currentMsgs]
      }
    }
  }),

  addMessage: (ticketId, message) => set((state) => {
    const currentMsgs = state.messages[ticketId] || []
    if (currentMsgs.some(m => m.id === message.id)) return state
    return {
      messages: {
        ...state.messages,
        [ticketId]: [...currentMsgs, message]
      }
    }
  }),

  setPresence: (key, presence) => {
    if (!key) return;

    if (presenceTimers.has(key)) {
      clearTimeout(presenceTimers.get(key)!)
      presenceTimers.delete(key)
    }

    if (!presence || presence === 'paused') {
      set((state) => {
        if (!state.presences[key]) return state
        const next = { ...state.presences }
        delete next[key]
        return { presences: next }
      })
    } else {
      set((state) => ({
        presences: {
          ...state.presences,
          [key]: presence
        }
      }))

      // Auto-expira a presença após 7 segundos se não houver novo evento
      const timer = setTimeout(() => {
        set((state) => {
          if (!state.presences[key]) return state
          const next = { ...state.presences }
          delete next[key]
          return { presences: next }
        })
        presenceTimers.delete(key)
      }, 7000)

      presenceTimers.set(key, timer)
    }
  }
}))
